"use strict";
import { useQueryClient } from "@tanstack/react-query";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import { DEFAULT_TXN_DISMISS_MS, L2_TXN_DISMISS_MS } from "constants/misc";
import { useCallback } from "react";
import { usePollPendingBatchTransactions } from "state/activity/polling/batch";
import { usePollPendingBridgeTransactions } from "state/activity/polling/bridge";
import { usePollPendingOrders } from "state/activity/polling/orders";
import { usePollPendingTransactions } from "state/activity/polling/transactions";
import { useAppDispatch } from "state/hooks";
import { updateSignature } from "state/signatures/reducer";
import { SignatureType } from "state/signatures/types";
import {
  addTransaction,
  applyTransactionHashToBatch,
  confirmBridgeDeposit,
  finalizeTransaction
} from "state/transactions/reducer";
import { TransactionType } from "state/transactions/types";
import { logSwapFinalized, logUniswapXSwapFinalized } from "tracing/swapFlowLoggers";
import { UniswapXOrderStatus } from "types/uniswapx";
import { TransactionStatus } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { isL2ChainId } from "features/chains/utils";
import {
  areCurrencyIdsEqual,
  buildNativeCurrencyId,
  buildWrappedNativeCurrencyId,
  currencyId,
  currencyIdToChain
} from "utils/uniswapCurrencyId";
import { ReactQueryCacheKey } from "utilities/src/reactQuery/cache";
import { useTrace } from "utilities/src/telemetry/trace/TraceContext";
export function ActivityStateUpdater() {
  const onActivityUpdate = useOnActivityUpdate();
  return <>
      {
    /* The polling updater is present to update activity states for chains that are not supported by the subscriptions service. */
  }
      <PollingActivityStateUpdater onActivityUpdate={onActivityUpdate} />
    </>;
}
function PollingActivityStateUpdater({ onActivityUpdate }) {
  usePollPendingTransactions(onActivityUpdate);
  usePollPendingBatchTransactions(onActivityUpdate);
  usePollPendingBridgeTransactions(onActivityUpdate);
  usePollPendingOrders(onActivityUpdate);
  return null;
}
function useOnActivityUpdate() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const analyticsContext = useTrace();
  return useCallback(
    (activity) => {
      const popupDismissalTime = isL2ChainId(activity.chainId) ? L2_TXN_DISMISS_MS : DEFAULT_TXN_DISMISS_MS;
      if (activity.type === "transaction") {
        const { chainId, original, update } = activity;
        if (activity.original.batchInfo && update.hash) {
          dispatch(
            applyTransactionHashToBatch({ batchId: activity.original.batchInfo.batchId, chainId, hash: update.hash })
          );
        }
        const hash = update.hash ?? original.hash;
        if (original.info.type === TransactionType.BRIDGE && !original.info.depositConfirmed && update.status === TransactionStatus.Confirmed) {
          dispatch(confirmBridgeDeposit({ chainId, hash, ...update }));
          return;
        }
        dispatch(finalizeTransaction({ chainId, hash, ...update }));
        const batchId = original.batchInfo?.batchId;
        if (original.info.type === TransactionType.SWAP) {
          if (update.status === TransactionStatus.Confirmed) {
            const impactedCurrencyIds = getImpactedSwapBalanceCurrencyIds(
              original.info.inputCurrencyId,
              original.info.outputCurrencyId
            );
            void queryClient.invalidateQueries({
              predicate: (query) => shouldInvalidateSwapBalanceQuery(query.queryKey, original.from, impactedCurrencyIds)
            });
          }
          logSwapFinalized(hash, batchId, chainId, chainId, analyticsContext, update.status, original.info.type);
        } else if (original.info.type === TransactionType.BRIDGE) {
          logSwapFinalized(
            hash,
            batchId,
            original.info.inputChainId,
            original.info.outputChainId,
            analyticsContext,
            update.status,
            original.info.type
          );
        }
        popupRegistry.addPopup({ type: PopupType.Transaction, hash }, hash, popupDismissalTime);
      } else if (activity.type === "signature") {
        const { chainId, original, update } = activity;
        if (original.status === UniswapXOrderStatus.FILLED) {
          return;
        }
        const updatedOrder = { ...original, ...update };
        dispatch(updateSignature(updatedOrder));
        const signatureType = updatedOrder.type ?? SignatureType.SIGN_UNISWAPX_V2_ORDER;
        if (updatedOrder.status === UniswapXOrderStatus.FILLED) {
          const hash = updatedOrder.txHash;
          const from = original.offerer;
          dispatch(addTransaction({ chainId, from, hash, info: updatedOrder.swapInfo }));
          popupRegistry.addPopup({ type: PopupType.Transaction, hash }, hash, popupDismissalTime);
          if (original.type !== SignatureType.SIGN_LIMIT) {
            logUniswapXSwapFinalized(
              hash,
              updatedOrder.orderHash,
              chainId,
              analyticsContext,
              signatureType,
              UniswapXOrderStatus.FILLED
            );
          }
        } else if (original.status !== updatedOrder.status) {
          const orderHash = original.orderHash;
          popupRegistry.addPopup({ type: PopupType.Order, orderHash }, orderHash, popupDismissalTime);
          if (updatedOrder.status === UniswapXOrderStatus.CANCELLED || updatedOrder.status === UniswapXOrderStatus.EXPIRED) {
            logUniswapXSwapFinalized(
              void 0,
              updatedOrder.orderHash,
              chainId,
              analyticsContext,
              signatureType,
              updatedOrder.status
            );
          }
        }
      }
    },
    [analyticsContext, dispatch, queryClient]
  );
}
function getImpactedSwapBalanceCurrencyIds(inputCurrencyId, outputCurrencyId) {
  const currencyIds = /* @__PURE__ */ new Set([inputCurrencyId, outputCurrencyId]);
  for (const swapCurrencyId of [inputCurrencyId, outputCurrencyId]) {
    const chainId = currencyIdToChain(swapCurrencyId);
    if (!chainId) {
      continue;
    }
    const nativeCurrencyId = buildNativeCurrencyId(chainId);
    const wrappedNativeCurrencyId = buildWrappedNativeCurrencyId(chainId);
    if (areCurrencyIdsEqual(swapCurrencyId, nativeCurrencyId) || areCurrencyIdsEqual(swapCurrencyId, wrappedNativeCurrencyId)) {
      currencyIds.add(nativeCurrencyId);
      currencyIds.add(wrappedNativeCurrencyId);
    }
  }
  return Array.from(currencyIds);
}
function shouldInvalidateSwapBalanceQuery(queryKey, accountAddress, impactedCurrencyIds) {
  const [cacheKey, queryAccountAddress, currency] = queryKey;
  if (cacheKey !== ReactQueryCacheKey.OnchainBalances || typeof queryAccountAddress !== "string" || queryAccountAddress.toLowerCase() !== accountAddress.toLowerCase() || !isCurrency(currency)) {
    return false;
  }
  const queryCurrencyId = currencyId(currency);
  return impactedCurrencyIds.some((impactedCurrencyId) => areCurrencyIdsEqual(impactedCurrencyId, queryCurrencyId));
}
function isCurrency(value) {
  return Boolean(
    value && typeof value === "object" && "chainId" in value && "isNative" in value && "isToken" in value
  );
}
